import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { PracticeInterviewInsights } from "@type/interview";
import AuthLayout from "@container/layout/auth";
import HighlightTranscript from "@component/highlight-transcript";
import FeedbackOverview from "@container/feedback-overview";
import {
    ArrowLeftIcon,
    ClockIcon,
    FileTextIcon,
    MessageCircleIcon,
    MessageSquareIcon,
    CheckCircleIcon,
    AlertCircleIcon,
    VideoIcon,
    MicIcon,
    BarChart3,
    LightbulbIcon,
    MonitorIcon,
    AlertTriangleIcon,
    ThumbsUpIcon,
    SmileIcon,
    UsersIcon,
    HandIcon,
    LoaderIcon,
} from "lucide-react";
import { useHlsVideo } from "@hook/useHlsVideo";
import { Button } from "@component/button";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@component/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@component/tabs";
import {
    getCandidateInsights,
    getInterviewInsights,
    getVideo,
    isClassicGlobalInsights,
    isPracticeInterviewInsights,
    ClassicGlobalInsights,
} from "@service/interview";
import { getAssessment, getUserInterviewSubmission } from "@service/assessment";
import { Assessment, InterviewAssessment } from "@type/assessments";
import { useUserProfileContext } from "@context/user_profile";
import ourteamThumbnail from "@asset/ourteam_thumbnail.png";
import { InterviewInsight, InterviewVideo } from "@type/videoInterview";
import { formatDuration } from "@util/date";

type DetailInsights = ClassicGlobalInsights | PracticeInterviewInsights;

export default function CandidateInterviewFeedbackDetailPage() {
    const navigate = useNavigate();
    const params = useParams();
    const videoRef = useRef<HTMLVideoElement>(null);
    // Transcript is rendered inside a right-side drawer (Sheet) — open state
    // is controlled by this flag.
    const [isTranscriptOpen, setIsTranscriptOpen] = useState<boolean>(false);
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isLoadingVideo, setLoadingVideo] = useState<boolean>(true);
    const [isLoadingInsights, setLoadingInsights] = useState<boolean>(true);
    const [videoInterview, setVideoInterview] = useState<InterviewVideo | undefined>(undefined);
    // Prefer the streaming `video_url` (BE #3092) over the legacy download `s3_link`.
    const videoSrc = videoInterview?.video_url ?? videoInterview?.s3_link ?? null;
    // Stream HLS / progressive MP4 + expose a buffering state for the overlay.
    const { buffering } = useHlsVideo(videoRef, videoSrc);
    const [interviewDetail, setInterviewDetail] = useState<DetailInsights | null>(null);
    const [assessment, setAssessment] = useState<Assessment | undefined>(undefined);
    // Structured scoring breakdown + strengths/areas/recommendation copy —
    // same shape AiInterviewTabContent renders from getInterviewInsights.
    const [interviewInsights, setInterviewInsights] = useState<InterviewInsight | null>(null);
    const [isLoadingInterviewInsight, setLoadingInterviewInsight] = useState<boolean>(true);
    const [videoDuration, setVideoDuration] = useState<number | null>(null);

    useEffect(() => {
        if (params.interviewid) {
            getAssessment(params.interviewid).then((resp) => {
                if (resp?.id) {
                    setAssessment(resp);
                }
            });
        }
    }, [params.interviewid]);

    useEffect(() => {
        if (!isLoadingUserProfile && params.interviewid && userInfo?.id) {
            setLoadingInsights(true);
            setLoadingVideo(true);
            getUserInterviewSubmission(params.interviewid, userInfo?.id || "").then((response) => {
                getCandidateInsights(response.conversation_id || "")
                    .then((insightsResponse) => {
                        if (
                            insightsResponse?.status === "success" ||
                            insightsResponse?.status === "partial_success"
                        ) {
                            setInterviewDetail(insightsResponse.global_video_insights ?? null);
                        }
                    })
                    .finally(() => {
                        setLoadingInsights(false);
                    });

                // Parallel fetch — same endpoint AiInterviewTabContent reads from,
                // BE returns the structured InterviewInsight as a JSON string in
                // res.insights, so we parse it.
                setLoadingInterviewInsight(true);
                getInterviewInsights(response.conversation_id || "")
                    .then((res) => {
                        if (res?.insights) {
                            try {
                                setInterviewInsights(JSON.parse(res.insights));
                            } catch (parseErr) {
                                console.error("Failed to parse interview insights JSON:", parseErr);
                            }
                        }
                    })
                    .catch((err) => {
                        console.error("Failed to fetch interview insights:", err);
                    })
                    .finally(() => {
                        setLoadingInterviewInsight(false);
                    });

                getVideo(response.conversation_id)
                    .then((resp) => {
                        setVideoInterview(resp);
                        setLoadingVideo(false);
                    })
                    .catch((error) => {
                        console.error("failed to get video", error);
                        setLoadingVideo(false);
                    });
            });
        }
    }, [params, isLoadingUserProfile, userInfo]);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement || !videoSrc) return;

        const handleLoadedMetadata = () => {
            if (!isNaN(videoElement.duration)) {
                setVideoDuration(videoElement.duration);
            }
        };

        if (!isNaN(videoElement.duration) && videoElement.duration > 0) {
            setVideoDuration(videoElement.duration);
        }
        videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
        return () => {
            videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
        };
    }, [videoSrc, isLoadingVideo]);

    const isScored = isPracticeInterviewInsights(interviewDetail);
    const isClassic = isClassicGlobalInsights(interviewDetail);
    const scored = isScored ? (interviewDetail as PracticeInterviewInsights) : null;
    const classic = isClassic ? (interviewDetail as ClassicGlobalInsights) : null;


    const sortedInsights = classic
        ? [...(classic.globalInsights ?? [])].sort((a, b) => {
            if (a.type === "positive" && b.type === "suggestion") return -1;
            if (a.type === "suggestion" && b.type === "positive") return 1;
            return 0;
        })
        : [];

    // Demo-practice flag drives whether we surface both tabs (Feedback
    // + AI Interview) or fall back to the Feedback view only.
    const isDemoPracticeMode =
        import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true";

    // Used by the Feedback tab — just the player, no surrounding card,
    // header, duration line, or transcript trigger.
    const videoPlayer = (
        <div className="rounded-3xl overflow-hidden outline outline-1 outline-gray-200 dark:outline-gray-700">
            {isLoadingVideo ? (
                <div className="aspect-video w-full bg-neutral-100 animate-pulse" />
            ) : videoSrc ? (
                <div className="aspect-video w-full relative">
                    <video
                        ref={videoRef}
                        id="interview-video"
                        className="w-full h-full"
                        controls
                        controlsList="nodownload"
                        preload="metadata"
                        playsInline
                        onContextMenu={(e) => e.preventDefault()}
                        poster={ourteamThumbnail}
                    >
                        Your browser does not support the video tag.
                    </video>
                    {buffering && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                            <LoaderIcon className="h-8 w-8 animate-spin text-white" />
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );

    // The transcript / technical-analysis body — shared between the
    // Sheet drawer on the AI Interview tab and the inline block under
    // the video on the Feedback tab.
    const transcriptContent = isLoadingInsights ? (
        <TranscriptSkeleton />
    ) : scored ? (
        (scored.highlights ?? []).length === 0 ? (
            <EmptyState
                icon={MessageCircleIcon}
                title="Transcript not ready yet."
                description={(
                    <>
                        We're still preparing the transcript for this interview. <br /> Come back in a few minutes to see your full conversation.
                    </>
                )}
                tone="processing"
            />
        ) : (
            <>
                {(() => {
                    let questionCounter = 0;
                    const candidateName =
                        [userInfo?.first_name, userInfo?.last_name]
                            .filter(Boolean)
                            .join(" ")
                            .trim() || undefined;
                    const candidateAvatar = userInfo?.profile_picture_path
                        ? `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${userInfo.profile_picture_path}`
                        : undefined;
                    return (scored.highlights ?? []).map((segment, index) => {
                        const hasQuestion = (segment.question ?? "").trim().length > 0;
                        const questionNumber = hasQuestion
                            ? ++questionCounter
                            : undefined;
                        return (
                            <HighlightTranscript
                                key={index}
                                time={segment.time}
                                question={segment.question}
                                answer={segment.answer}
                                questionNumber={questionNumber}
                                candidateName={candidateName}
                                candidateAvatar={candidateAvatar}
                                isActive={index === 0}
                                onClick={() => { }}
                            />
                        );
                    });
                })()}
            </>
        )
    ) : classic ? (
        <ClassicTechnicalQuality classic={classic} />
    ) : (
        <EmptyState
            icon={MessageCircleIcon}
            title="Transcript not ready yet"
            description={(
                <>
                    We're still preparing the transcript for this interview. <br /> Come back in a few minutes to see your full conversation.
                </>
            )}
            tone="processing"
        />
    );

    // Used by the AI Interview tab — the full card with header, duration
    // footer, and "View transcript" drawer trigger (preserves the
    // existing AI Interview layout the user signed off on earlier).
    const videoCard = (
        <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4 space-y-3">
            <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                <MonitorIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                Interview Video Recording
            </h4>
            {videoPlayer}
            <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {videoSrc
                        ? (videoDuration !== null
                            ? (videoDuration < 60
                                ? `${Math.floor(videoDuration)} seconds`
                                : formatDuration(videoDuration))
                            : "Loading duration...")
                        : "Not recorded yet"}
                </div>
                <Sheet open={isTranscriptOpen} onOpenChange={setIsTranscriptOpen}>
                    <SheetTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                            {classic ? (
                                <FileTextIcon className="w-4 h-4 text-blue-600" />
                            ) : (
                                <MessageCircleIcon className="w-4 h-4 text-blue-600" />
                            )}
                            {classic ? "View technical analysis" : "View transcript"}
                        </Button>
                    </SheetTrigger>
                    <SheetContent
                        side="right"
                        className="w-full sm:max-w-xl flex flex-col p-0"
                    >
                        <SheetHeader className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
                            <SheetTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                {classic ? (
                                    <FileTextIcon className="w-4 h-4 text-blue-600" />
                                ) : (
                                    <MessageCircleIcon className="w-4 h-4 text-blue-600" />
                                )}
                                {classic ? "Technical analysis" : "Transcript"}
                            </SheetTitle>
                        </SheetHeader>
                        <div className="p-4 flex-1 min-h-0 overflow-y-auto">
                            {transcriptContent}
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </div>
    );

    return (
        <AuthLayout>
            <div className="-mt-6">
                <Button
                variant="ghost"
                size="sm"
                className="mb-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 -ml-2"
                onClick={() => {
                    navigate(`/interview/${params.interviewid}/feedback`);
                }}
            >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back to Interviews
            </Button>
            <div className="container mx-auto py-6 space-y-6">
                {scored && (scored.highlights ?? []).length === 0 && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                        <ClockIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
                        <p className="text-xs text-gray-600">
                            Video processing in progress. Refresh in 10-15 minutes.
                        </p>
                    </div>
                )}

                    {/* Demo mode → both tabs (Feedback + AI Interview).
                    Otherwise → render the Feedback view directly with
                    no tabs wrapper. */}
                    {!isDemoPracticeMode ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="lg:sticky lg:top-4 self-start space-y-4">
                                {videoPlayer}
                                <div className="rounded-lg bg-white dark:bg-gray-900 shadow-sm flex flex-col max-h-[60vh] overflow-hidden">
                                    <h4 className="font-medium text-sm text-gray-900 dark:text-white flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-shrink-0">
                                        {classic ? (
                                            <FileTextIcon className="w-4 h-4 text-blue-600" />
                                        ) : (
                                            <MessageCircleIcon className="w-4 h-4 text-blue-600" />
                                        )}
                                        {classic ? "Technical analysis" : "Transcript"}
                                    </h4>
                                    <div className="flex-1 min-h-0 overflow-y-auto p-4">
                                        {transcriptContent}
                                    </div>
                                </div>
                            </div>
                            <div>
                                {isLoadingInsights ? (
                                    <FeedbackSkeleton />
                                ) : scored ? (
                                    scored.feedbacks ? (
                                        <div className="rounded-lg bg-white dark:bg-gray-900 overflow-hidden shadow-sm">
                                            <FeedbackOverview
                                                feedback={scored.feedbacks}
                                                language={
                                                    assessment?.type === "Interview"
                                                        ? (assessment as InterviewAssessment).content?.interviewLanguage
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <NoFeedbackEmpty />
                                    )
                                ) : (
                                    <NoFeedbackEmpty />
                                )}
                            </div>
                        </div>
                    ) : (
                <Tabs defaultValue="feedback" className="w-full">
                    <TabsList className="bg-transparent h-auto p-0 space-x-0 w-full justify-start rounded-none border-b border-gray-200 dark:border-gray-700 mb-[10px]">
                        <TabsTrigger
                            value="feedback"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-600 text-gray-600 px-5 py-3 text-sm font-medium"
                        >
                            Feedback
                        </TabsTrigger>
                        <TabsTrigger
                            value="summaries"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-600 text-gray-600 px-5 py-3 text-sm font-medium"
                        >
                            AI Interview
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="feedback">
                        {/* Two-column layout on lg+: video on the left,
                            feedback overview on the right. Stacks
                            vertically on smaller viewports so the video
                            stays watchable on mobile. Feedback tab uses
                            the bare `videoPlayer` (no header / duration /
                            transcript trigger) — the AI Interview tab
                            still renders the full `videoCard`. */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="lg:sticky lg:top-4 self-start space-y-4">
                                {videoPlayer}
                                {/* Inline transcript below the video —
                                    mirrors what the Sheet drawer renders
                                    on the AI Interview tab, but always
                                    visible here. The header stays fixed
                                    while only the transcript body scrolls. */}
                                <div className="rounded-lg bg-white dark:bg-gray-900 shadow-sm flex flex-col max-h-[60vh] overflow-hidden">
                                    <h4 className="font-medium text-sm text-gray-900 dark:text-white flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-shrink-0">
                                        {classic ? (
                                            <FileTextIcon className="w-4 h-4 text-blue-600" />
                                        ) : (
                                            <MessageCircleIcon className="w-4 h-4 text-blue-600" />
                                        )}
                                        {classic ? "Technical analysis" : "Transcript"}
                                    </h4>
                                    <div className="flex-1 min-h-0 overflow-y-auto p-4">
                                        {transcriptContent}
                                    </div>
                                </div>
                            </div>
                            <div>
                                {isLoadingInsights ? (
                                    <FeedbackSkeleton />
                                ) : scored ? (
                                    scored.feedbacks ? (
                                        <div className="rounded-lg bg-white dark:bg-gray-900 overflow-hidden shadow-sm">
                                            <FeedbackOverview
                                                feedback={scored.feedbacks}
                                                language={
                                                    assessment?.type === "Interview"
                                                        ? (assessment as InterviewAssessment).content?.interviewLanguage
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <NoFeedbackEmpty />
                                    )
                                ) : (
                                    <NoFeedbackEmpty />
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="summaries" className="space-y-6">
                        {/* Interview Scoring Breakdown — mirrors AiInterviewTabContent. */}
                        {interviewInsights?.interview_score
                            && interviewInsights.interview_score.length > 0 && (
                                <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <h4 className="font-medium text-base flex items-center">
                                                <BarChart3 className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                                Interview Scoring Breakdown
                                            </h4>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Based on weighted evaluation across {interviewInsights.interview_score.length} competencies.
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">Total Interview Score:</span>
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-base font-semibold">
                                                {interviewInsights.total_interview_score ?? 0}/100
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-4 space-y-4">
                                        {interviewInsights.interview_score.map((scoreItem, index) => (
                                            <div key={index} className="space-y-1.5">
                                                <div className="flex items-center justify-start gap-2">
                                                    <span className="text-sm font-medium">{scoreItem.title}</span>
                                                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                                        {scoreItem.score}/{scoreItem.max_score}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {scoreItem.explanation}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        {/* AI Insights & Recommendations */}
                        {isLoadingInterviewInsight && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                <h4 className="font-medium text-base flex items-center mb-4">
                                    <LightbulbIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                    AI Insights & Recommendations
                                </h4>
                                <div className="flex items-center justify-center h-32">
                                    <svg
                                        className="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" stroke="currentColor"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4.93 4.93a10 10 0 0114.14 14.14L12 12z"></path>
                                    </svg>
                                </div>
                            </div>
                        )}

                        {!isLoadingInterviewInsight && interviewInsights && (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                <h4 className="font-medium text-base flex items-center mb-4">
                                    <LightbulbIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                    AI Insights & Recommendations
                                </h4>
                                <div className="space-y-4">
                                    {interviewInsights.strengths && (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
                                                <span className="font-medium text-sm text-green-800 dark:text-green-200">
                                                    Strengths
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.strengths}
                                            </p>
                                        </div>
                                    )}
                                    {interviewInsights.areas_for_growth && (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <AlertTriangleIcon className="h-4 w-4 mr-2 text-orange-600 dark:text-orange-400" />
                                                <span className="font-medium text-sm text-orange-800 dark:text-orange-200">
                                                    Areas for Growth
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.areas_for_growth}
                                            </p>
                                        </div>
                                    )}
                                    {interviewInsights.recommendation && (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <ThumbsUpIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                                <span className="font-medium text-sm text-blue-800 dark:text-blue-200">
                                                    Overall Recommendation
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.recommendation}
                                            </p>
                                        </div>
                                    )}
                                    {interviewInsights.interpersonal_skills && (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <SmileIcon className="h-4 w-4 mr-2 text-purple-600 dark:text-purple-400" />
                                                <span className="font-medium text-sm text-purple-800 dark:text-purple-200">
                                                    Interpersonal Skills
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.interpersonal_skills}
                                            </p>
                                        </div>
                                    )}
                                    {interviewInsights.collaboration_potential && (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <UsersIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                                <span className="font-medium text-sm text-blue-800 dark:text-blue-200">
                                                    Collaboration Potential
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.collaboration_potential}
                                            </p>
                                        </div>
                                    )}
                                    {interviewInsights.leadership_potential && (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center">
                                                <HandIcon className="h-4 w-4 mr-2 text-orange-600 dark:text-orange-400" />
                                                <span className="font-medium text-sm text-orange-800 dark:text-orange-200">
                                                    Leadership Potential
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {interviewInsights.leadership_potential}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Video card lives below the AI insights stack
                            on this tab, mirroring the Feedback tab so
                            both surfaces show the recording. */}
                        {videoCard}
                    </TabsContent>
                </Tabs>
                    )}
                </div>
            </div>
        </AuthLayout>
    );
}

function TranscriptSkeleton() {
    return (
        <div className="space-y-6 animate-pulse" aria-busy aria-label="Loading transcript">
            {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="space-y-3">
                    {/* Time chip placeholder */}
                    <div className="flex justify-center">
                        <div className="h-4 w-12 bg-neutral-200 rounded-full" />
                    </div>
                    {/* Interviewer bubble */}
                    <div className="flex items-end gap-2">
                        <div className="w-7 h-7 rounded-full bg-neutral-200 flex-shrink-0" />
                        <div className="space-y-1 w-2/3">
                            <div className="h-2.5 w-16 bg-neutral-200 rounded" />
                            <div className="rounded-2xl rounded-bl-sm bg-neutral-100 px-3.5 py-2 space-y-1.5">
                                <div className="h-3 bg-neutral-200 rounded w-full" />
                                <div className="h-3 bg-neutral-200 rounded w-4/5" />
                            </div>
                        </div>
                    </div>
                    {/* Candidate bubble */}
                    <div className="flex items-end gap-2 justify-end">
                        <div className="space-y-1 w-2/3 flex flex-col items-end">
                            <div className="h-2.5 w-16 bg-neutral-200 rounded" />
                            <div className="rounded-2xl rounded-br-sm bg-neutral-200 px-3.5 py-2 space-y-1.5 w-full">
                                <div className="h-3 bg-neutral-300 rounded w-full" />
                                <div className="h-3 bg-neutral-300 rounded w-3/4" />
                            </div>
                        </div>
                        <div className="w-7 h-7 rounded-full bg-neutral-200 flex-shrink-0" />
                    </div>
                </div>
            ))}
        </div>
    );
}

function FeedbackSkeleton() {
    return (
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 animate-pulse" aria-busy aria-label="Loading feedback">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-neutral-200 rounded" />
                    <div className="h-4 w-32 bg-neutral-200 rounded" />
                </div>
                <div className="w-6 h-5 bg-neutral-200 rounded-full" />
            </div>
            {/* Filter row */}
            <div className="px-5 py-3 border-b border-gray-100 space-y-2">
                <div className="h-2.5 w-28 bg-neutral-200 rounded" />
                <div className="flex flex-wrap gap-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-7 w-20 bg-neutral-100 rounded-full border border-neutral-200" />
                    ))}
                </div>
            </div>
            {/* Content */}
            <div className="px-5 py-4 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3 py-2">
                        <div className="w-2 h-2 mt-1.5 rounded-full bg-neutral-200 flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                            <div className="h-3.5 bg-neutral-200 rounded w-3/4" />
                            <div className="h-3 bg-neutral-100 rounded w-1/2" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NoFeedbackEmpty({ embedded = false }: { embedded?: boolean }) {
    const inner = (
        <EmptyState
            icon={MessageSquareIcon}
            title="Feedback being prepared."
            description="We're crafting personalized feedback based on your responses. This usually takes a few minutes — refresh the page to check again."
            tone="processing"
        />
    );

    if (embedded) return inner;
    return (
        <div className="rounded-lg bg-white dark:bg-gray-900 h-full overflow-hidden">
            {/* Header — mirrors FeedbackOverview so the section keeps
                the same affordance even when there's no data yet. */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <MessageSquareIcon className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900">Review feedback</h3>
            </div>
            {inner}
        </div>
    );
}

interface EmptyStateProps {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: React.ReactNode;
    tone?: "processing" | "neutral";
}

function EmptyState({ icon: Icon, title, description, tone = "processing" }: EmptyStateProps) {
    const isProcessing = tone === "processing";
    const containerClass = isProcessing
        ? "bg-blue-50 text-blue-600"
        : "bg-gray-100 text-gray-400";

    return (
        <div className="py-12 px-6 text-center">
            <span
                className={`inline-flex w-14 h-14 rounded-full items-center justify-center mb-4 ${containerClass}`}
            >
                <Icon className="h-6 w-6" />
            </span>
            <h3 className="text-base font-semibold text-gray-900 mb-1.5">{title}</h3>
            <div className="text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
                {description}
            </div>
        </div>
    );
}

function ClassicTechnicalQuality({ classic }: { classic: ClassicGlobalInsights }) {
    const video = classic.globalVideoQuality ?? [];
    const audio = classic.globalAudioQuality ?? [];

    if (video.length === 0 && audio.length === 0) {
        return (
            <EmptyState
                icon={FileTextIcon}
                title="No technical analysis yet."
                description="We haven't been able to evaluate the audio and video quality for this interview yet."
                tone="neutral"
            />
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <VideoIcon className="h-4 w-4 text-blue-500" />
                    <h3 className="font-medium">Video Quality</h3>
                </div>
                <div className="space-y-3 pl-6">
                    {video.length > 0 ? (
                        video.map((item, index) => (
                            <div key={index} className="space-y-1">
                                <h4 className="text-sm font-medium">{item.title}</h4>
                                <p className="text-sm text-gray-600">{item.description}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500">No video quality data.</p>
                    )}
                </div>
            </div>
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <MicIcon className="h-4 w-4 text-blue-500" />
                    <h3 className="font-medium">Audio Quality</h3>
                </div>
                <div className="space-y-3 pl-6">
                    {audio.length > 0 ? (
                        audio.map((item, index) => (
                            <div key={index} className="space-y-1">
                                <h4 className="text-sm font-medium">{item.title}</h4>
                                <p className="text-sm text-gray-600">{item.description}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500">No audio quality data.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
